import { createRoute, Link } from '@tanstack/react-router'
import { rootRoute, requireAuth } from './__root'
import { useHomeData, useApprovalsList, useCCSessions, useVisibilityRefetch } from '../api/hooks'

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: requireAuth,
  component: Home,
})

function formatUptime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  const remMins = mins % 60
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`
}

function sessionStatusColour(status: string): string {
  if (status === 'idle' || status === 'processing') return 'bg-green-900/50 text-green-300'
  if (status === 'reconnecting') return 'bg-yellow-900/50 text-yellow-300'
  return 'bg-gray-800 text-gray-400'
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function Home() {
  const home = useHomeData()
  const approvals = useApprovalsList()
  const ccsessions = useCCSessions()
  useVisibilityRefetch(['dashboard'])

  if (home.isLoading || approvals.isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <p className="text-gray-400">Loading...</p>
      </div>
    )
  }

  if (home.error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <p className="text-red-400">Failed to load: {home.error.message}</p>
      </div>
    )
  }

  const counts = home.data?.counts ?? { awaitingApproval: 0, failedCronsLast24h: 0, upcomingCronsNextHour: 0 }
  const recentActivity = home.data?.recentActivity ?? []
  const allClear = counts.awaitingApproval === 0 && counts.failedCronsLast24h === 0

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Dashboard</h1>

      <div className="flex gap-4">
        {/* Action Queue */}
        <div className="flex-1 bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
            Action Queue
          </h2>
          {allClear ? (
            <p className="text-gray-500 text-sm">Nothing in queue</p>
          ) : (
            <ul className="space-y-2">
              {counts.awaitingApproval > 0 && (
                <li className="flex items-center justify-between text-sm">
                  <Link
                    to="/approvals"
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {counts.awaitingApproval} awaiting approval
                  </Link>
                  <span className="text-xs bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded">
                    review
                  </span>
                </li>
              )}
              {counts.failedCronsLast24h > 0 && (
                <li className="flex items-center justify-between text-sm">
                  <span className="text-red-400">
                    {counts.failedCronsLast24h} cron{counts.failedCronsLast24h !== 1 ? 's' : ''} failed (24h)
                  </span>
                  <span className="text-xs bg-red-900/50 text-red-300 px-2 py-0.5 rounded">
                    failed
                  </span>
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Live State */}
        <div className="flex-1 bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Live State
            </h2>
            <p className="text-sm text-gray-200">
              {counts.upcomingCronsNextHour === 0
                ? 'No crons in the next hour'
                : `${counts.upcomingCronsNextHour} cron${counts.upcomingCronsNextHour !== 1 ? 's' : ''} queued in next hour`}
            </p>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              CC Sessions
            </h3>
            {ccsessions.isLoading ? (
              <div className="space-y-1.5">
                {[0, 1].map((i) => (
                  <div key={i} className="h-5 bg-gray-800 rounded animate-pulse w-3/4" />
                ))}
              </div>
            ) : ccsessions.data?.sessions.length === 0 ? (
              <p className="text-xs text-gray-600">No active sessions</p>
            ) : (
              <ul className="space-y-1.5">
                {(ccsessions.data?.sessions ?? []).map((s) => (
                  <li key={s.name} className="flex items-center justify-between text-xs">
                    <span className="text-gray-300 font-mono truncate">{s.name}</span>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`px-1.5 py-0.5 rounded font-medium ${sessionStatusColour(s.status)}`}>
                        {s.status}
                      </span>
                      <span className="text-gray-500">{formatUptime(s.uptime)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
          Recent Activity
        </h2>
        {recentActivity.length === 0 ? (
          <p className="text-gray-500 text-sm">No recent activity</p>
        ) : (
          <ul className="divide-y divide-gray-800">
            {recentActivity.map((item) => (
              <li key={item.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-gray-300 font-mono">{item.event_type}</span>
                <span className="text-gray-500 text-xs">{formatRelativeTime(item.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
